import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Práce o samotě u zákazníka - V1 DRAFT - authored for structural
 * completeness; NOT a substitute for sign-off by a Czech osoba odborně
 * způsobilá v prevenci rizik. Legal refs: zákoník práce §5 + §102 (prevence
 * rizik covers osamocená práce). MARKETPLACE-SPECIFIC: FixIt workers routinely
 * work alone in customers' homes - this lesson covers check-in routine,
 * incident readiness and conflict de-escalation. Bilingual (cs-CZ + en-US).
 * Three chapters (rizika o samotě | check-in rutina | konfliktní situace) + a
 * graded quiz with one question per kind. Answer keys live here
 * (repo-privileged) and in the DB; stripped from the client payload.
 */
export const loneWorkingProfile: TrainingProfile = {
  slug: 'lone-working',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar column.
  title: 'Práce o samotě u zákazníka',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-rizika',
        title: t('Rizika práce o samotě', 'The risks of working alone'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-rizika',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Sám v cizím bytě', "Alone in a stranger's home"),
          },
          {
            id: 'p-rizika-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Většinu zakázek děláš **sám v cizím prostředí**. To znamená dvě specifická rizika: **úraz bez okamžité pomoci** (pád ze žebříku, zásah proudem - nikdo nezavolá 155 za tebe) a **konfliktní či nekomfortní situace** se zákazníkem. Obojí se řídí předem, ne až když nastane.',
              'Most jobs you do **alone in an unfamiliar place**. That means two specific risks: **an injury with no immediate help** (a ladder fall, an electric shock - nobody calls the ambulance for you) and **conflict or discomfort** with a customer. Both are managed in advance, not once they happen.',
            ),
          },
          {
            id: 'c-rizika-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **zákon č. 309/2006 Sb. §5 odst. 1 písm. e)** (organizace práce tak, aby zaměstnanec nepracoval osamoceně tam, kde hrozí zvláštní nebezpečí) a **zákoník práce §102** - prevence rizik zahrnuje i práci osamoceného pracovníka; rizikové činnosti (elektro pod napětím, motorová pila, uzavřené prostory) se **o samotě nedělají vůbec**.',
              'Legal basis: **Act 309/2006 Coll. §5(1)(e)** (work must be organised so that nobody works alone where a particular hazard exists) and **Labour Code §102** - risk prevention includes lone work; high-risk activities (live electrical work, chainsaws, confined spaces) are **never done alone at all**.',
            ),
          },
        ],
      },
      {
        id: 'ch-checkin',
        title: t('Check-in rutina', 'The check-in routine'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-checkin'],
        blocks: [
          {
            id: 'h-checkin',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Ať někdo ví, kde jsi', 'Make sure someone knows where you are'),
          },
          {
            id: 'anim-checkin',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'checkInLoop',
            steps: [
              {
                label: t('Příjezd', 'Arrival'),
                say: t(
                  'Stav „Na místě“ potvrzen v aplikaci.',
                  'Status "On site" confirmed in the app.',
                ),
              },
              {
                label: t('Kontrolní čas', 'Check time'),
                say: t(
                  'Delší práce: domluvený čas kontroly.',
                  'Longer job: an agreed check-in time.',
                ),
              },
              {
                label: t('Bez odezvy → poplach', 'No reply → alarm'),
                say: t(
                  'Nepotvrzený kontrolní čas spustí eskalaci na dispečink.',
                  'A missed check-in escalates to dispatch.',
                ),
              },
            ],
            caption: t(
              'Smyčka: příjezd potvrzený v aplikaci → průběžný kontakt → odjezd. Když se neozveš, někdo jedná.',
              'The loop: arrival confirmed in the app → periodic contact → departure. If you go silent, someone acts.',
            ),
          },
          {
            id: 'p-checkin-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Používej stavy zakázky ve FixIt aplikaci (**Na cestě → Na místě → Hotovo**) jako svůj check-in: fungují zároveň jako záznam, kde ses kdy nacházel. U delších prací si domluv **kontrolní čas** s někým blízkým. Měj v telefonu rychlou volbu **155/158** a nabitou baterii.',
              'Use the FixIt job states (**Driving → On site → Done**) as your check-in: they double as a record of where you were and when. For longer jobs agree a **check time** with someone close. Keep **155/158** on speed dial and your battery charged.',
            ),
          },
          {
            id: 'kc-checkin',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Proč potvrzovat stavy zakázky (Na místě / Hotovo) hned, ne zpětně?',
              'Why confirm job states (On site / Done) immediately, not retroactively?',
            ),
            choices: [
              { id: 'kc-a', label: t('Kvůli rychlejší fakturaci', 'For faster invoicing') },
              {
                id: 'kc-b',
                label: t(
                  'Je to záznam, kde právě jsem - funguje jako check-in',
                  'It records where I am right now - it works as a check-in',
                ),
              },
              {
                id: 'kc-c',
                label: t('Jen kvůli statistikám aplikace', "Just for the app's statistics"),
              },
            ],
            correctChoiceId: 'kc-b',
            explanation: t(
              'Aktuální stav zakázky je jediná stopa, kterou má kdokoli k dispozici, když se přestaneš ozývat.',
              'The live job state is the only trace anyone has if you go silent.',
            ),
          },
        ],
      },
      {
        id: 'ch-konflikt',
        title: t('Konfliktní situace', 'Conflict situations'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-konflikt'],
        blocks: [
          {
            id: 'h-konflikt',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Odejít je legitimní', 'Leaving is legitimate'),
          },
          {
            id: 'p-konflikt-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Když se situace u zákazníka stane **agresivní nebo nekomfortní** (opilost, výhrůžky, nátlak): **nehádej se o zakázku**. Mluv klidně, drž si **volnou cestu ke dveřím**, a když se to nelepší, **sbal se a odejdi** - bezpečnost má vždy přednost před dokončením práce. Incident pak nahlas přes aplikaci; FixIt stojí za tebou.',
              "If a situation turns **aggressive or uncomfortable** (intoxication, threats, pressure): **don't argue over the job**. Speak calmly, keep a **clear path to the door**, and if it doesn't improve, **pack up and leave** - safety always beats finishing the work. Report the incident in the app afterwards; FixIt backs you.",
            ),
          },
          {
            id: 'ord-konflikt',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď reakci na eskalující konflikt u zákazníka.',
              'Order the response to an escalating customer conflict.',
            ),
            items: [
              { id: 'ok-1', label: t('Zklidnit tón, nehádat se', "Lower the tone, don't argue") },
              { id: 'ok-2', label: t('Držet si volnou cestu k odchodu', 'Keep a clear exit path') },
              { id: 'ok-3', label: t('Sbalit se a odejít', 'Pack up and leave') },
              {
                id: 'ok-4',
                label: t('Nahlásit incident přes aplikaci', 'Report the incident in the app'),
              },
            ],
          },
          {
            id: 'k-konflikt',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              '**Žádná zakázka nestojí za tvé bezpečí.** Odchod z nebezpečné situace není selhání - je to správný postup.',
              '**No job is worth your safety.** Leaving a dangerous situation is not a failure - it is the correct procedure.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-konflikt-single',
      kind: 'single',
      chapterId: 'ch-konflikt',
      prompt: t(
        'Zákazník je opilý a začíná ti vyhrožovat. Co uděláš?',
        'The customer is drunk and starts threatening you. What do you do?',
      ),
      choices: [
        { id: 'a', label: t('Dodělám práci co nejrychleji', 'Finish the job as fast as possible') },
        {
          id: 'b',
          label: t(
            'Klidně ukončím práci a odejdu, pak to nahlásím',
            'Calmly stop working and leave, then report it',
          ),
        },
        {
          id: 'c',
          label: t('Vyhrožování oplatím, ať si nedovoluje', 'Threaten back so he backs off'),
        },
      ],
      correct: 'b',
    },
    {
      id: 'q-rizikove-tf',
      kind: 'truefalse',
      chapterId: 'ch-rizika',
      prompt: t(
        'Práci na elektrickém zařízení pod napětím lze dělat o samotě, pokud jsem rychlý.',
        'Live electrical work may be done alone as long as I am quick.',
      ),
      correct: false,
    },
    {
      id: 'q-checkin-multi',
      kind: 'multi',
      chapterId: 'ch-checkin',
      prompt: t(
        'Co patří do check-in rutiny? (vyber všechny správné)',
        'What belongs to the check-in routine? (select all that apply)',
      ),
      choices: [
        {
          id: 'a',
          label: t('Potvrzené stavy zakázky v aplikaci', 'Confirmed job states in the app'),
        },
        {
          id: 'b',
          label: t(
            'Nabitá baterie a rychlá volba 155/158',
            'Charged battery and 155/158 speed dial',
          ),
        },
        { id: 'c', label: t('Vypnutý telefon, ať neruší', "Phone off so it doesn't disturb") },
        {
          id: 'd',
          label: t('Domluvený kontrolní čas u delších prací', 'Agreed check time for longer jobs'),
        },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-policie-numeric',
      kind: 'numeric',
      chapterId: 'ch-konflikt',
      prompt: t(
        'Jaké číslo má tísňová linka Policie ČR?',
        'What is the Czech Police emergency number?',
      ),
      correct: 158,
      tolerance: 0,
    },
    {
      id: 'q-konflikt-order',
      kind: 'ordering',
      chapterId: 'ch-konflikt',
      prompt: t(
        'Seřaď reakci na eskalující konflikt.',
        'Order the response to an escalating conflict.',
      ),
      choices: [
        { id: 'a', label: t('Zklidnit tón', 'Lower the tone') },
        { id: 'b', label: t('Volná cesta k odchodu', 'Clear exit path') },
        { id: 'c', label: t('Odejít', 'Leave') },
        { id: 'd', label: t('Nahlásit incident', 'Report the incident') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
